import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Mail } from 'lucide-react';
import { BRAND } from '../../utils/brand';
import { CONTACT } from '../../config';
import { useLocalizedUrl } from '../../utils/urlHelper';

// Pie B2B (nov-2026): azul marino como el resto de la web, columna de marca con
// el contacto y enlaces sobrios. Fuera la llamada «Solicitar demo» con botón
// redondeado: repetía el cierre de la home y el header ya lleva «Contacto».

const TAGLINE = {
  es: 'Documentos de transporte digitales con plena validez legal, para el transporte nacional e internacional.',
  en: 'Digital transport documents with full legal validity, for domestic and international transport.',
};

const LINK = 'text-slate-300 hover:text-white transition-colors';

const FooterColumn = ({ title, children }) => (
  <div>
    <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-4">{title}</h3>
    <ul className="space-y-2.5 text-sm">{children}</ul>
  </div>
);

const Footer = () => {
  const { t, i18n } = useTranslation(['footer', 'common']);
  const isSpanish = i18n.language === 'es';
  const lang = isSpanish ? 'es' : 'en';
  const { getLocalizedUrl } = useLocalizedUrl();
  // «BLUECMR, S.L.U.» ya acaba en punto: no añadir otro
  const legal = BRAND.legalName || BRAND.name;
  const copyrightName = legal.endsWith('.') ? legal : `${legal}.`;

  return (
    <footer className="text-white border-t border-white/10" style={{ backgroundColor: '#081a33' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Marca y contacto */}
          <div className="lg:col-span-3">
            <Link to={isSpanish ? '/' : '/en'} className="text-2xl leading-none tracking-tight text-white hover:opacity-80 transition-opacity" style={{ fontWeight: 900 }}>
              {BRAND.shortName}
            </Link>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-xs">{TAGLINE[lang]}</p>
            <ul className="mt-6 space-y-2 text-sm">
              <li className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <a href={CONTACT.phoneHref} className={LINK}>{CONTACT.phoneDisplay}</a>
              </li>
              <li className="pl-[26px] text-slate-500 text-xs">{CONTACT.phoneHours[lang]}</li>
              <li className="flex items-center gap-2.5 pt-1">
                <Mail className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <a href={`mailto:${CONTACT.email}`} className={LINK}>{CONTACT.email}</a>
              </li>
            </ul>
          </div>

          {/* Enlaces: mismas columnas en los dos idiomas (4-nov-2026, versión inglesa
              completa); en inglés no hay artículos, que solo existen en español. */}
          <div className="lg:col-span-9 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-10">
            <FooterColumn title={isSpanish ? 'Soluciones' : 'Solutions'}>
              <li><Link to={getLocalizedUrl('/cargadores')} className={LINK}>{isSpanish ? 'Cargadores' : 'Shippers'}</Link></li>
              <li><Link to={getLocalizedUrl('/transportistas')} className={LINK}>{isSpanish ? 'Transportistas' : 'Carriers'}</Link></li>
              <li><Link to={getLocalizedUrl('/proveedores-software')} className={LINK}>{isSpanish ? 'Proveedores de software' : 'Software providers'}</Link></li>
            </FooterColumn>

            <FooterColumn title={isSpanish ? 'Plataforma' : 'Platform'}>
              <li><Link to={getLocalizedUrl('/plataforma')} className={LINK}>{t('footer:como-funciona')}</Link></li>
              <li><Link to={getLocalizedUrl('/interoperabilidad')} className={LINK}>{t('footer:interoperabilidad', 'Integraciones')}</Link></li>
              <li><Link to={getLocalizedUrl('/confianza/firma-electronica')} className={LINK}>{t('footer:firma')}</Link></li>
              <li><Link to={getLocalizedUrl('/confianza/seguridad')} className={LINK}>{t('footer:seguridad', 'Seguridad')}</Link></li>
              <li><a href={`/${i18n.language}/developers/info`} target="_blank" rel="noopener noreferrer" className={LINK}>API Docs</a></li>
            </FooterColumn>

            <FooterColumn title={t('footer:documentos')}>
              <li><Link to={getLocalizedUrl('/documentos/ecmr')} className={LINK}>eCMR</Link></li>
              <li><Link to={getLocalizedUrl('/documentos/carta-porte')} className={LINK}>{isSpanish ? t('footer:carta-porte') : 'Consignment note (Spain)'}</Link></li>
              <li><Link to={getLocalizedUrl('/documentos/documento-control')} className={LINK}>{isSpanish ? t('footer:doc-control') : 'DeCA (Spain)'}</Link></li>
              <li><Link to={getLocalizedUrl('/documentos/transporte-adr')} className={LINK}>{isSpanish ? 'Documento de transporte ADR' : 'ADR transport document'}</Link></li>
              {isSpanish && <li><Link to="/articulos" className={LINK}>{t('footer:articulos-guias')}</Link></li>}
            </FooterColumn>

            <FooterColumn title={t('footer:empresa')}>
              <li><Link to={getLocalizedUrl('/quienes-somos')} className={LINK}>{t('footer:quienes-somos')}</Link></li>
              <li><Link to={getLocalizedUrl('/confianza/cumplimiento-normativo')} className={LINK}>{t('footer:cumplimiento', 'Cumplimiento normativo')}</Link></li>
              <li><Link to={getLocalizedUrl('/contacto')} className={LINK}>{t('footer:contacto')}</Link></li>
            </FooterColumn>

            <FooterColumn title={t('footer:legal')}>
              <li><Link to={getLocalizedUrl('/aviso-legal')} className={LINK}>{t('footer:aviso-legal')}</Link></li>
              <li><Link to={getLocalizedUrl('/terminos')} className={LINK}>{t('footer:terminos')}</Link></li>
              <li><Link to={getLocalizedUrl('/privacidad')} className={LINK}>{isSpanish ? t('footer:politica-privacidad') : 'Privacy policy'}</Link></li>
              <li><Link to={getLocalizedUrl('/cookies')} className={LINK}>{isSpanish ? t('footer:politica-cookies') : 'Cookie policy'}</Link></li>
              <li><Link to={getLocalizedUrl('/dpa')} className={LINK}>{t('footer:dpa')}</Link></li>
              <li>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('open-cookie-settings'))}
                  className={`${LINK} text-left`}
                >
                  {isSpanish ? 'Configurar cookies' : 'Cookie settings'}
                </button>
              </li>
            </FooterColumn>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs text-slate-500">
          <p>© 2026 {copyrightName} {t('footer:copyright')}</p>
          <p>{t('footer:made-in')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
